import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Button, Col, Form, Modal, Nav, Row, Spinner, Tab } from "react-bootstrap";
import { FiSend } from "react-icons/fi";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { ErrorResponse } from "@core/types/errorResponse";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { useInviteActions } from "@features/admin/invitations/hooks/useInviteActions";
import { useInviteNewUserMatch } from "@features/admin/invitations/hooks/useInviteNewUserMatch";
import { SendInvitationModalProps } from "@features/invitations/types/sendInvitationModalProps";
import { useInvitationOptions } from "@features/admin/invitations/hooks/useInvitationOptions";
import { getScopedActionCode } from "@features/entity/types/actionCodes";
import RequirePermission from "@core/components/RequirePermission";
import { Role } from "@features/invitations/types/roleResponse";
import { getEffectiveScopeFromParams } from "@features/admin/invitations/utils/getEffectiveScopeFromParams";
import { SendInvitationRequest } from "@features/invitations/types/sendInvitationRequest";
import { getApiScopeFromValue, getScopeFromValue } from "@features/invitations/utils/getScopeFromParam";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import InfoTooltip from "@core/components/InfoTooltip";
import { usePermissions } from "@features/entity/hooks/usePermissions";
import { EntityUser } from "@features/user/types/user";
import InviteDestinationField from "@features/admin/invitations/components/InviteDestinationField";
import InviteExistingUserField from "@features/admin/invitations/components/InviteExistingUserField";
import {
    INVITE_EXISTING_USER_REQUIRED,
    INVITE_NEW_MATCH_ACTION,
    INVITE_NEW_MATCH_HINT,
    INVITE_NEW_MATCH_TITLE,
    INVITE_SCOPE_WORD,
    INVITE_TAB_EXISTING,
    INVITE_TAB_NEW,
    InvitePersonTab,
    encodeInviteDestination,
    parseInviteDestination,
} from "@features/admin/invitations/constants/inviteModal";
import {
    isRoleValidForDepartment,
    rolesForDepartment,
} from "@features/admin/invitations/constants/inviteEntityPicker";
import DepartmentSelectOptions from "@features/department/components/DepartmentSelectOptions";

type FieldErrors = Partial<Record<string, string[]>>;

/**
 * Modal for sending user invitations. Supports cross-level invites: an admin can invite
 * at their own level or below (nation -> district/church, district -> church) by choosing
 * a destination. Roles are loaded for the target scope and filtered by department rules;
 * departments are loaded at the inviter's own scope. Existing-user search requires
 * VIEW_*_USERS_LIST. Destination can be controlled by the parent for table ↔ modal sync.
 */
const SendInvitationModal: React.FC<SendInvitationModalProps> = ({
    show,
    onHide,
    scope: scopeProp,
    scopeRefId: scopeRefIdProp,
    scopeName,
    onSent,
    destinationScope,
    destinationScopeRefId,
    destinationName: destinationNameProp,
    onDestinationChange,
}) => {
    const [searchParams] = useSearchParams();
    const fromParams = getEffectiveScopeFromParams(searchParams);
    const ownScopeValue = scopeProp ?? fromParams.effectiveScope;
    const ownScopeRefId = Number(scopeRefIdProp ?? fromParams.effectiveScopeRefId) || 0;

    const ownScope = useMemo(() => getScopeFromValue(ownScopeValue), [ownScopeValue]);
    const ownApiScope = useMemo(() => getApiScopeFromValue(ownScopeValue), [ownScopeValue]);
    const ownLabel = scopeName ?? `This ${INVITE_SCOPE_WORD[ownScope] ?? "level"}`;

    const isDestinationControlled =
        !!destinationScope &&
        destinationScopeRefId != null &&
        Number(destinationScopeRefId) > 0;

    const [destinationValue, setDestinationValue] = useState("");
    const [destinationName, setDestinationName] = useState<string | undefined>(undefined);
    const [personTab, setPersonTab] = useState<InvitePersonTab>(INVITE_TAB_NEW);
    const [selectedUser, setSelectedUser] = useState<EntityUser | null>(null);

    const parsedDestination = useMemo(
        () => parseInviteDestination(destinationValue),
        [destinationValue]
    );
    const targetScope = parsedDestination?.scope || ownScope;
    const targetScopeRefId = parsedDestination?.scopeRefId || ownScopeRefId;
    const apiTargetScope = getApiScopeFromValue(targetScope);

    const [form, setForm] = useState<SendInvitationRequest>({
        email: "",
        trn: "",
        role: "",
        department_id: 0,
    });
    const [formErrors, setFormErrors] = useState<FieldErrors>({});
    const [sendAnother, setSendAnother] = useState(false);
    const emailInputRef = useRef<HTMLInputElement>(null);
    const showError = useApiError();
    const showValidation = useErrorModalStore((s) => s.showValidation);
    const { busy, doCreate } = useInviteActions();

    const viewUsersAction = getScopedActionCode(ownScope, "VIEW_USERS");
    const { hasPermission, loading: viewUsersLoading } = usePermissions({
        scopeType: ownScope || undefined,
        scopeId: ownScopeRefId > 0 ? ownScopeRefId : undefined,
        actionCodes: viewUsersAction ? [viewUsersAction] : [],
    });
    const canInviteExisting =
        show && !viewUsersLoading && !!viewUsersAction && hasPermission(viewUsersAction);

    const matchEnabled =
        show && canInviteExisting && personTab === INVITE_TAB_NEW && !!ownScope && ownScopeRefId > 0;
    const { matchedUser } = useInviteNewUserMatch(
        ownScope,
        ownScopeRefId,
        matchEnabled,
        form.email,
        form.trn
    );
    const blockNewUserSend = personTab === INVITE_TAB_NEW && matchedUser != null;

    const { departments, depsLoading, depsError, fetchDepartments, roles, rolesLoading, rolesError, fetchRoles } =
        useInvitationOptions({
            scope: ownApiScope,
            scopeRefId: ownScopeRefId,
        });

    const filteredRoles = useMemo(
        () =>
            rolesForDepartment(
                roles.filter((r: Role) => r.scope === targetScope),
                Number(form.department_id) || 0
            ),
        [roles, targetScope, form.department_id]
    );

    useEffect(() => {
        if (!show || !targetScope) return;
        fetchRoles({ scope: targetScope });
    }, [targetScope, fetchRoles, show]);

    useEffect(() => {
        if (!show || !ownApiScope || !(ownScopeRefId > 0)) return;
        fetchDepartments();
    }, [ownApiScope, ownScopeRefId, fetchDepartments, show]);

    // Seed / sync destination from controlled parent (table) or default to own workspace.
    useEffect(() => {
        if (!show || !ownScope || !(ownScopeRefId > 0)) return;
        if (isDestinationControlled) {
            const encoded = encodeInviteDestination(
                getScopeFromValue(destinationScope!) || destinationScope!,
                Number(destinationScopeRefId)
            );
            setDestinationValue(encoded);
            setDestinationName(destinationNameProp);
            return;
        }
        setDestinationValue((current) => {
            if (current && parseInviteDestination(current)) return current;
            return encodeInviteDestination(ownScope, ownScopeRefId);
        });
        setDestinationName((current) => current ?? ownLabel);
    }, [
        show,
        ownScope,
        ownScopeRefId,
        ownLabel,
        isDestinationControlled,
        destinationScope,
        destinationScopeRefId,
        destinationNameProp,
    ]);

    // Clear role when it becomes invalid for the selected department.
    useEffect(() => {
        if (!form.role) return;
        const selected = roles.find((r) => r.profile_code === form.role);
        if (!isRoleValidForDepartment(selected, Number(form.department_id) || 0)) {
            setForm((f) => ({ ...f, role: "" }));
        }
    }, [form.department_id, form.role, roles]);

    // If Existing tab becomes unavailable, fall back to New user.
    useEffect(() => {
        if (!canInviteExisting && personTab === INVITE_TAB_EXISTING) {
            setPersonTab(INVITE_TAB_NEW);
            setSelectedUser(null);
        }
    }, [canInviteExisting, personTab]);

    const emailValid = /\S+@\S+\.\S+/.test(form.email);

    const handleChange = (field: keyof SendInvitationRequest) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        let value: string | number = e.target.value;
        if (field === "department_id") value = value === "" ? 0 : Number(value);
        setForm((f) => ({ ...f, [field]: value }));
    };

    const clearIdentity = useCallback(() => {
        setForm((f) => ({ ...f, email: "", trn: "" }));
        setSelectedUser(null);
        setFormErrors((prev) => {
            const next = { ...prev };
            delete next.email;
            delete next.trn;
            delete next.existingUser;
            return next;
        });
    }, []);

    const resetForm = useCallback(() => {
        setForm({ email: "", trn: "", role: "", department_id: 0 });
        setFormErrors({});
        setPersonTab(INVITE_TAB_NEW);
        setSelectedUser(null);
        setSendAnother(false);
        if (isDestinationControlled && destinationScope && destinationScopeRefId) {
            setDestinationValue(
                encodeInviteDestination(
                    getScopeFromValue(destinationScope) || destinationScope,
                    Number(destinationScopeRefId)
                )
            );
            setDestinationName(destinationNameProp);
        } else if (ownScope && ownScopeRefId > 0) {
            setDestinationValue(encodeInviteDestination(ownScope, ownScopeRefId));
            setDestinationName(ownLabel);
        } else {
            setDestinationValue("");
            setDestinationName(undefined);
        }
    }, [
        ownScope,
        ownScopeRefId,
        ownLabel,
        isDestinationControlled,
        destinationScope,
        destinationScopeRefId,
        destinationNameProp,
    ]);

    const resetFormForNextInvite = () => {
        if (sendAnother) {
            clearIdentity();
            requestAnimationFrame(() => {
                if (personTab === INVITE_TAB_NEW) emailInputRef.current?.focus();
            });
        } else {
            resetForm();
        }
    };

    const handlePersonTabSelect = (key: string | null) => {
        if (!key || (key !== INVITE_TAB_NEW && key !== INVITE_TAB_EXISTING)) return;
        if (key === personTab) return;
        setPersonTab(key);
        clearIdentity();
    };

    const handleExistingUserSelect = (user: EntityUser | null) => {
        setSelectedUser(user);
        if (user) {
            setForm((f) => ({ ...f, email: user.email, trn: user.trn }));
            setFormErrors((prev) => {
                const next = { ...prev };
                delete next.email;
                delete next.trn;
                delete next.existingUser;
                return next;
            });
        } else {
            setForm((f) => ({ ...f, email: "", trn: "" }));
        }
    };

    /** Switch to Existing with the matched directory user; keep destination/dept/role. */
    const handleInviteMatchedUser = () => {
        if (!matchedUser) return;
        setPersonTab(INVITE_TAB_EXISTING);
        setSelectedUser(matchedUser);
        setForm((f) => ({ ...f, email: matchedUser.email, trn: matchedUser.trn }));
        setFormErrors((prev) => {
            const next = { ...prev };
            delete next.email;
            delete next.trn;
            delete next.existingUser;
            return next;
        });
    };

    const submit = async () => {
        if (blockNewUserSend) return;

        try {
            const messages: string[] = [];
            const nextErrors: FieldErrors = {};

            if (personTab === INVITE_TAB_EXISTING) {
                if (!selectedUser) {
                    messages.push(INVITE_EXISTING_USER_REQUIRED);
                    nextErrors.existingUser = [INVITE_EXISTING_USER_REQUIRED];
                }
            }

            if (!emailValid) {
                messages.push("Please enter a valid email address.");
                nextErrors.email = ["Please enter a valid email address."];
            }
            if (!form.trn?.trim()) {
                messages.push("TRN is required.");
                nextErrors.trn = ["TRN is required."];
            }
            if (!form.role?.trim()) {
                messages.push("Role is required.");
                nextErrors.role = ["Role is required."];
            }
            if (!(targetScopeRefId > 0) || !targetScope) {
                messages.push("Please choose where to invite this person.");
                nextErrors.target = ["Please choose a destination."];
            }
            if (!form.department_id || form.department_id === 0) {
                messages.push("Department is required.");
                nextErrors.department_id = ["Department is required."];
            }
            if (messages.length > 0) {
                setFormErrors(nextErrors);
                showValidation(messages);
                return;
            }

            const payload: SendInvitationRequest = {
                email: form.email,
                trn: form.trn,
                role: form.role,
                department_id: Number(form.department_id),
            };

            const response = await doCreate(apiTargetScope, targetScopeRefId, payload);

            if (response.isSuccess()) {
                const joinName =
                    destinationName || scopeName || `your ${INVITE_SCOPE_WORD[ownScope] ?? "entity"}`;
                showSuccess(`Invitation sent to ${payload.email}${joinName ? ` to join ${joinName}` : ""}.`);
                if (!sendAnother) onHide();
                onSent?.({ scope: targetScope, scopeRefId: targetScopeRefId, name: joinName });
                resetFormForNextInvite();
            } else {
                const error = response.getError() as ErrorResponse;
                const fieldErrors = (error?.errors as FieldErrors) ?? {};
                setFormErrors(fieldErrors);
                const list =
                    fieldErrors && typeof fieldErrors === "object"
                        ? Object.values(fieldErrors).flat().filter(Boolean)
                        : [error?.detail || "Failed to send invitation"];
                showValidation(list.length ? (list as string[]) : [error?.detail || "Failed to send invitation"]);
                showError(error);
            }
        } catch (err) {
            showError(err as ErrorResponse);
        }
    };

    useEffect(() => {
        if (!show) {
            setForm({ email: "", trn: "", role: "", department_id: 0 });
            setFormErrors({});
            setPersonTab(INVITE_TAB_NEW);
            setSelectedUser(null);
            setSendAnother(false);
            setDestinationValue("");
            setDestinationName(undefined);
        }
    }, [show]);

    const sendAction = getScopedActionCode(targetScope, "SEND_INVITE");

    return (
        <Modal
            show={show}
            onHide={onHide}
            backdrop="static"
            keyboard={false}
            centered
            size="lg"
            data-testid="send-invitation-modal"
        >
            <Modal.Header closeButton className="border-0 pb-0" data-testid="modal-header">
                <Modal.Title className="fw-semibold" data-testid="modal-title">
                    <FiSend className="me-2" aria-hidden />
                    Send Invitation
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="pt-3" data-testid="modal-body">
                <Form data-testid="invitation-form">
                    <Tab.Container activeKey={personTab} onSelect={handlePersonTabSelect}>
                        {canInviteExisting && (
                            <Nav variant="tabs" className="mb-3" data-testid="invite-person-tabs">
                                <Nav.Item>
                                    <Nav.Link eventKey={INVITE_TAB_NEW} data-testid="invite-tab-new">
                                        New user
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey={INVITE_TAB_EXISTING} data-testid="invite-tab-existing">
                                        Existing user
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>
                        )}

                        <Tab.Content>
                            <Tab.Pane eventKey={INVITE_TAB_NEW}>
                                {(personTab === INVITE_TAB_NEW || !canInviteExisting) && (
                                    <>
                                        <Row className="g-3 mb-3">
                                            <Col md={6}>
                                                <Form.Group controlId="invEmail">
                                                    <Form.Label>
                                                        Email address<span className="text-danger">*</span>
                                                        <InfoTooltip text="The email where the invitation link will be sent. The user will sign in with this address." />
                                                    </Form.Label>
                                                    <Form.Control
                                                        ref={emailInputRef}
                                                        type="email"
                                                        placeholder="name@company.com"
                                                        value={form.email}
                                                        onChange={handleChange("email")}
                                                        isInvalid={!!formErrors.email}
                                                        autoFocus
                                                        required
                                                        data-testid="input-email"
                                                    />
                                                    <Form.Control.Feedback
                                                        type="invalid"
                                                        role="alert"
                                                        data-testid="input-email-error"
                                                    >
                                                        {formErrors.email?.[0]}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group controlId="invTrn">
                                                    <Form.Label>
                                                        TRN<span className="text-danger">*</span>
                                                        <InfoTooltip text="Tax Registration Number – a unique identifier for the invitee in the system." />
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Enter TRN"
                                                        value={form.trn}
                                                        onChange={handleChange("trn")}
                                                        isInvalid={!!formErrors.trn}
                                                        data-testid="input-trn"
                                                    />
                                                    <Form.Control.Feedback
                                                        type="invalid"
                                                        role="alert"
                                                        data-testid="input-trn-error"
                                                    >
                                                        {formErrors.trn?.[0]}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        {matchedUser && (
                                            <Alert
                                                variant="info"
                                                className="mb-3"
                                                data-testid="invite-new-user-match-banner"
                                            >
                                                <div className="fw-semibold">{INVITE_NEW_MATCH_TITLE}</div>
                                                <div className="small mt-1">
                                                    {[matchedUser.fname, matchedUser.lname]
                                                        .filter(Boolean)
                                                        .join(" ")
                                                        .trim() ||
                                                        matchedUser.username}{" "}
                                                    · {matchedUser.email} · TRN {matchedUser.trn}
                                                </div>
                                                <div className="small mt-2">{INVITE_NEW_MATCH_HINT}</div>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    className="mt-3 w-100"
                                                    onClick={handleInviteMatchedUser}
                                                    data-testid="invite-new-user-match-action"
                                                >
                                                    {INVITE_NEW_MATCH_ACTION}
                                                </Button>
                                            </Alert>
                                        )}
                                    </>
                                )}
                            </Tab.Pane>
                            <Tab.Pane eventKey={INVITE_TAB_EXISTING}>
                                {personTab === INVITE_TAB_EXISTING && canInviteExisting && (
                                    <div className="mb-3">
                                        <InviteExistingUserField
                                            ownScope={ownScope}
                                            ownScopeRefId={ownScopeRefId}
                                            enabled={show && personTab === INVITE_TAB_EXISTING}
                                            selectedUser={selectedUser}
                                            onSelect={handleExistingUserSelect}
                                            isInvalid={!!formErrors.existingUser}
                                            errorText={formErrors.existingUser?.[0]}
                                        />
                                    </div>
                                )}
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>

                    <Row className="g-3">
                        <Col xs={12}>
                            <InviteDestinationField
                                ownScopeValue={ownScopeValue}
                                ownScopeRefId={ownScopeRefId}
                                ownLabel={ownLabel}
                                value={destinationValue}
                                enabled={show}
                                isInvalid={!!formErrors.target}
                                errorText={formErrors.target?.[0]}
                                onChange={({ scope, scopeRefId, name }) => {
                                    setDestinationValue(encodeInviteDestination(scope, scopeRefId));
                                    setDestinationName(name);
                                    setForm((f) => ({ ...f, role: "" }));
                                    onDestinationChange?.({ scope, scopeRefId, name });
                                }}
                            />
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="invDept">
                                <Form.Label>
                                    Department<span className="text-danger">*</span>
                                    <InfoTooltip text="The department this user will belong to. They will only see data for this department." />
                                </Form.Label>
                                <Form.Select
                                    value={form.department_id}
                                    onChange={handleChange("department_id")}
                                    isInvalid={!!formErrors.department_id}
                                    required
                                    data-testid="select-department"
                                >
                                    <option value={0}>Select Department</option>
                                    {depsLoading && <option value={0}>Loading departments…</option>}
                                    {depsError && <option disabled>Failed to load departments</option>}
                                    {!depsLoading && !depsError && (
                                        <DepartmentSelectOptions departments={departments} />
                                    )}
                                </Form.Select>
                                <Form.Control.Feedback
                                    type="invalid"
                                    role="alert"
                                    data-testid="select-department-error"
                                >
                                    {formErrors.department_id?.[0]}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="invRole">
                                <Form.Label>
                                    Role<span className="text-danger">*</span>
                                    <InfoTooltip text="The role defines what the user can do: view reports, submit data, manage users, etc. Available roles depend on the selected department." />
                                </Form.Label>
                                <Form.Select
                                    value={form.role}
                                    onChange={handleChange("role")}
                                    isInvalid={!!formErrors.role}
                                    required
                                    data-testid="select-role"
                                >
                                    <option value="">Select Role</option>
                                    {rolesLoading && <option>Loading roles…</option>}
                                    {rolesError && <option disabled>Failed to load roles</option>}
                                    {!rolesLoading &&
                                        !rolesError &&
                                        filteredRoles.map((r: Role) => (
                                            <option key={r.profile_code} value={r.profile_code}>
                                                {r.profile_description}
                                            </option>
                                        ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid" role="alert" data-testid="select-role-error">
                                    {formErrors.role?.[0]}
                                </Form.Control.Feedback>
                                {rolesError && (
                                    <div className="text-danger small mt-1" data-testid="roles-error">
                                        {rolesError}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Check
                        type="checkbox"
                        id="sendAnother"
                        label="Send another"
                        checked={sendAnother}
                        onChange={(e) => setSendAnother(e.target.checked)}
                        className="mt-4 mb-2"
                        data-testid="send-another-checkbox"
                    />

                    <div className="d-flex justify-content-between align-items-center">
                        <p className="text-muted small fst-italic mb-0" aria-live="polite" data-testid="confirmation-message">
                            <i className="bi bi-info-circle me-1" /> A confirmation will be emailed to the invitee.
                        </p>
                        <RequirePermission
                            mode="disable"
                            action={sendAction}
                            scopeType={targetScope}
                            scopeId={targetScopeRefId}
                        >
                            <Button
                                variant="primary"
                                size="lg"
                                className="btn-action"
                                onClick={submit}
                                disabled={busy || blockNewUserSend}
                                aria-busy={busy}
                                data-testid="submit-invitation"
                            >
                                {busy ? (
                                    <>
                                        <Spinner
                                            size="sm"
                                            animation="border"
                                            className="me-2"
                                            data-testid="sending-spinner"
                                        />
                                        Sending…
                                    </>
                                ) : (
                                    <>
                                        <FiSend className="me-2" />
                                        Send Invitation
                                    </>
                                )}
                            </Button>
                        </RequirePermission>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default SendInvitationModal;
